import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EmailIcon,
  EyeIcon,
  EyeOffIcon,
  AlertCircleIcon,
  ProfileIcon,
  ArrowDownIcon,
} from "../../assets/icons";
import {
  validateForm,
  validateFieldRealTime,
  clearFieldError,
} from "../../utils/validation";
import { Api } from "../../network/api";
import {
  setUsers,
  setUsersLoading,
  setUsersError,
  clearUsersError,
} from "../../redux/slice/globalSlice";

const AddUserModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.global);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const inputClass = `
    w-full px-5 py-4 border-2 rounded-2xl transition-all duration-300
    bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500
    border-gray-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20
    focus:outline-none
  `;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    is_admin: false,
  });

  const [errors, setErrors] = useState({});

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(
      () => setNotification({ show: false, type: "", message: "" }),
      5000
    );
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const validation = validateFieldRealTime(field, value, formData);
    if (validation.isValid) {
      setErrors((prev) => clearFieldError(prev, field));
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: validation.message,
      }));
    }
  };

  const validateFormData = () => {
    const validation = validateForm(formData, [
      "name",
      "email",
      "password",
      "confirmPassword",
    ]);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      return;
    }

    setLoading(true);
    dispatch(setUsersLoading(true));
    dispatch(clearUsersError());

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password,
      is_admin: formData.is_admin,
    };

    try {
      const response = await Api.createUser(payload);
      console.log("Add user response:", response);
      console.log("Add user payload:", payload);
      const newUser = {
        id: response.data?.id || Date.now().toString(),
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.is_admin ? "Admin" : "User",
        createdAt: new Date().toLocaleDateString(),
      };

      dispatch(setUsers([newUser, ...users]));
      dispatch(setUsersLoading(false));

      const userType = formData.is_admin ? "Admin" : "User";
      showNotification("success", `${userType} added successfully!`);

      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        is_admin: false,
      });

      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess(newUser);
      }, 2000);
    } catch (error) {
      console.error("Error creating user:", error);
      dispatch(
        setUsersError(error.response?.data?.detail || "Failed to create user")
      );
      dispatch(setUsersLoading(false));
      showNotification(
        "error",
        error.response?.data?.detail || "Failed to create user"
      );
    }

    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;

    const hasData = Object.values(formData).some(
      (value) => value && value !== false && value !== ""
    );

    if (hasData) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl w-full max-w-lg mx-auto animate-fadeInUp rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              <div className="flex flex-col items-center justify-center">
                <h2 className="text-2xl font-bold text-white">
                  Add New {formData.is_admin ? "Admin" : "User"}
                </h2>
                <p className="text-red-100 text-sm font-medium">
                  Create a new user or admin account
                </p>
              </div>
            </div>
          </div>
        </div>
        {notification.show && (
          <div
            className={`mx-6 mt-6 p-4 rounded-xl border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-400 text-green-700"
                : "bg-red-50 border-red-400 text-red-700"
            }`}
          >
            <div className="flex items-center space-x-3">
              <AlertCircleIcon width="20" height="20" />
              <span className="font-medium">{notification?.message}</span>
            </div>
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className="px-8 py-8 space-y-6 flex-1 overflow-y-auto scrollbar-hide"
        >
          <div className="space-y-4">
            <label className="flex text-lg font-semibold text-gray-900">
              Account Type
            </label>
            <div className="relative">
              <select
                value={formData?.is_admin ? "admin" : "user"}
                onChange={(e) =>
                  handleInputChange("is_admin", e.target.value === "admin")
                }
                className={`${inputClass} px-4 py-3 appearance-none cursor-pointer`}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <ArrowDownIcon />
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <label className="flex text-lg font-semibold text-gray-900">
              Full Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`${inputClass} ${
                  errors.name ? "border-red-300 focus:border-red-500" : ""
                }`}
                placeholder="Enter full name"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <ProfileIcon />
              </div>
            </div>
            {errors.name && (
              <p className="text-sm text-red-600 flex items-center space-x-2">
                <AlertCircleIcon width="16" height="16" />
                <span>{errors?.name}</span>
              </p>
            )}
          </div>
          <div className="space-y-4">
            <label className="flex text-lg font-semibold text-gray-900">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={formData.email}
                autoComplete="off"
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`${inputClass} ${
                  errors.email ? "border-red-300 focus:border-red-500" : ""
                }`}
                placeholder="Enter email address"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <EmailIcon width="20" height="20" className="text-gray-400" />
              </div>
            </div>
            {errors.email && (
              <p className="text-sm text-red-600 flex items-center space-x-2">
                <AlertCircleIcon width="16" height="16" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          <div className="space-y-4">
            <label className="flex text-lg font-semibold text-gray-900">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                autoComplete="off"
                onChange={(e) => handleInputChange("password", e.target.value)}
                className={`${inputClass} pr-12 ${
                  errors.password ? "border-red-300 focus:border-red-500" : ""
                }`}
                placeholder="Enter password"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showPassword ? (
                    <EyeOffIcon width="20" height="20" />
                  ) : (
                    <EyeIcon width="20" height="20" />
                  )}
                </button>
              </div>
            </div>
            {errors.password && (
              <p className="text-sm text-red-600 flex items-center space-x-2">
                <AlertCircleIcon width="16" height="16" />
                <span>{errors.password}</span>
              </p>
            )}
          </div>

          <div className="space-y-4">
            <label className="flex text-lg font-semibold text-gray-900">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                className={`${inputClass} pr-12 ${
                  errors.confirmPassword
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                placeholder="Confirm password"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon width="20" height="20" />
                  ) : (
                    <EyeIcon width="20" height="20" />
                  )}
                </button>
              </div>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-600 flex items-center space-x-2">
                <AlertCircleIcon width="16" height="16" />
                <span>{errors.confirmPassword}</span>
              </p>
            )}
          </div>
        </form>
        <div className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50/50">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-3 rounded-2xl font-medium transition-all duration-200 bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg hover:from-red-700 hover:to-red-900 hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Adding...</span>
                </div>
              ) : (
                <span>Add {formData.is_admin ? "Admin" : "User"}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
